import React, { useState } from "react";
import {
  MessageCircle,
  Phone,
  Send,
  CheckCircle,
  Globe,
  Users,
  Shield,
  Clock,
} from "lucide-react";

const toAlignClass = (a) => {
  const v = String(a || "").toLowerCase().trim();
  if (v === "left") return "text-left";
  if (v === "right") return "text-right";
  if (v === "center") return "text-center";
  return "";
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 135;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const toDigits = (s) => String(s || "").replace(/\D/g, "");

const DEFAULTS = {
  badgeText: "Connect With Us 24/7",

  title: "Get in Touch Instantly",
  subtitle: "Choose your preferred way to connect with our team. We're here to help!",

  tabWhatsApp: "WhatsApp",
  tabWhatsAppHint: "Instant reply",
  tabPhone: "Call Us",
  tabPhoneHint: "Talk to us directly",

  buttonText: "Send via WhatsApp",

  successTitle: "Opening WhatsApp...",
  successSubtitle: "Your message is ready to send",

  whatsappFormTitle: "WhatsApp Business",
  whatsappCardTitle: "WhatsApp Business",
  whatsappCardSubtitle: "Typically replies within minutes",
  whatsappStartText: "Open WhatsApp",
  whatsappFooterLine: "Mobile and web supported",

  namePlaceholder: "Your Name",
  phonePlaceholder: "Your Phone Number",
  messagePlaceholder: "How can we help you?",

  phoneTitle: "Call Our Support Team",
  phoneHours: "We are available during business hours",
  phoneCallNowText: "Call Us Now",
  phoneRatesText: "Call the business directly",

  footerStats: ["Global support", "Fast replies", "Secure and private"],
  trustBadges: ["⭐ Trusted service", "🔒 Private", "⚡ Fast support", "💬 WhatsApp available"],
};

function buildWhatsAppText(cfg, formData) {
  return [
    `*${cfg.title || "New Contact Request"}*`,
    "",
    `Name: ${formData.name || "-"}`,
    `Phone: ${formData.phone || "-"}`,
    `Message: ${formData.message || "-"}`,
  ].join("\n");
}

export default function CTASection2({ config, style, businessPhone }) {
  const cfg = { ...DEFAULTS, ...(config || {}) };

  const [activeTab, setActiveTab] = useState("whatsapp");
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "", message: "" });

  const bg = buildBg(style, "bg-gradient-to-br from-green-600 to-blue-700");

  const direction = (cfg.direction || "ltr").toLowerCase();

  const align = (style?.textAlign || "").trim();
  const hasAlign = align.length > 0;
  const headerAlignClass = hasAlign ? toAlignClass(align) : "text-center";
  const contentAlignClass = hasAlign ? toAlignClass(align) : "";

  const rawPrimary = cfg.buttonText;
  const showPrimary = rawPrimary === undefined ? true : String(rawPrimary).trim().length > 0;
  const primaryLabel = rawPrimary === undefined ? "Send via WhatsApp" : rawPrimary;

  const primaryHasCustom = Boolean(
    String(style?.primaryBtnBg || "").trim() || String(style?.primaryBtnText || "").trim()
  );

  const targetPhone = toDigits(
    businessPhone ||
      config?.businessPhone ||
      cfg.businessPhone ||
      cfg.whatsappNumber ||
      ""
  );

  const handleChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!targetPhone) {
      alert("Business WhatsApp phone is missing. Please add it in General tab.");
      return;
    }

    if (!formData.name || !formData.phone || !formData.message) return;

    const text = encodeURIComponent(buildWhatsAppText(cfg, formData));
    setSubmitted(true);
    window.open(`https://wa.me/${targetPhone}?text=${text}`, "_blank");

    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: "", phone: "", message: "" });
    }, 1800);
  };

  const handlePhoneCall = () => {
    if (!targetPhone) {
      alert("Business phone is missing. Please add it in General tab.");
      return;
    }
    window.location.href = `tel:+${targetPhone}`;
  };

  const mainCardStyle = {
    backgroundColor: style?.ctaCardBg || undefined,
    borderColor: style?.ctaCardBorder || undefined,
    borderWidth: style?.ctaCardBorder ? "1px" : undefined,
  };

  const panelStyle = {
    backgroundColor: style?.ctaPanelBg || undefined,
    borderColor: style?.ctaPanelBorder || undefined,
    borderWidth: style?.ctaPanelBorder ? "1px" : undefined,
  };

  const infoCardStyle = {
    backgroundColor: style?.ctaInfoCardBg || undefined,
    borderColor: style?.ctaInfoCardBorder || undefined,
    borderWidth: style?.ctaInfoCardBorder ? "1px" : undefined,
  };

  const badgeStyle = {
    backgroundColor: style?.ctaBadgeBg || undefined,
    color: style?.ctaBadgeText || undefined,
    borderColor: style?.ctaBadgeBorder || undefined,
    borderWidth: style?.ctaBadgeBorder ? "1px" : undefined,
  };

  const inputStyle = {
    backgroundColor: style?.ctaInputBg || undefined,
    borderColor: style?.ctaInputBorder || undefined,
    color: style?.ctaInputText || undefined,
  };

  const footerBarStyle = {
    backgroundColor: style?.ctaFooterBarBg || undefined,
    color: style?.ctaFooterTextColor || undefined,
  };

  const getTabStyle = (active) => ({
    backgroundColor: active
      ? style?.ctaTabActiveBg || undefined
      : style?.ctaTabInactiveBg || undefined,
    color: active
      ? style?.ctaTabActiveText || undefined
      : style?.ctaTabInactiveText || undefined,
    borderColor: style?.ctaTabBorder || undefined,
  });

  const quickItemStyle = {
    backgroundColor: style?.ctaQuickItemBg || undefined,
    borderColor: style?.ctaQuickItemBorder || undefined,
    color: style?.ctaQuickItemText || undefined,
  };

  return (
    <section style={bg.style} className={`py-14 px-4 ${bg.className}`} dir={direction}>
      <div className="container mx-auto max-w-5xl">
        <div className={`${headerAlignClass} mb-12`}>
          <div
            style={badgeStyle}
            className="inline-flex items-center bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full mb-6 border"
          >
            <MessageCircle className="w-5 h-5 mr-2 ml-2" />
            <span className="font-medium">{cfg.badgeText}</span>
          </div>

          <h2
            style={{ color: style?.titleColor || undefined }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            {cfg.title}
          </h2>

          <p
            style={{ color: style?.subtitleColor || undefined }}
            className="text-xl text-white/90"
          >
            {cfg.subtitle}
          </p>
        </div>

        <div style={mainCardStyle} className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="grid grid-cols-2 divide-x">
            <button
              onClick={() => setActiveTab("whatsapp")}
              style={getTabStyle(activeTab === "whatsapp")}
              className={`py-5 px-4 flex flex-col items-center transition-all ${
                activeTab === "whatsapp"
                  ? "bg-green-50 text-green-600"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100"
              }`}
            >
              <MessageCircle className="w-8 h-8 mb-2" />
              <span className="font-semibold">{cfg.tabWhatsApp}</span>
              <span
                style={{ color: style?.ctaMutedTextColor || undefined }}
                className="text-xs mt-1 text-gray-500"
              >
                {cfg.tabWhatsAppHint}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("phone")}
              style={getTabStyle(activeTab === "phone")}
              className={`py-5 px-4 flex flex-col items-center transition-all ${
                activeTab === "phone"
                  ? "bg-blue-50 text-blue-600"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100"
              }`}
            >
              <Phone className="w-8 h-8 mb-2" />
              <span className="font-semibold">{cfg.tabPhone}</span>
              <span
                style={{ color: style?.ctaMutedTextColor || undefined }}
                className="text-xs mt-1 text-gray-500"
              >
                {cfg.tabPhoneHint}
              </span>
            </button>
          </div>

          <div className={`p-8 ${contentAlignClass}`}>
            {submitted ? (
              <div className="py-12">
                <div
                  style={{ backgroundColor: style?.ctaSuccessIconBg || undefined }}
                  className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <CheckCircle
                    style={{ color: style?.ctaSuccessIconColor || undefined }}
                    className="w-10 h-10 text-green-600"
                  />
                </div>
                <h3
                  style={{ color: style?.ctaSuccessTitleColor || undefined }}
                  className="text-2xl font-bold text-gray-900 mb-2"
                >
                  {cfg.successTitle}
                </h3>
                <p
                  style={{
                    color: style?.ctaSuccessSubtitleColor || style?.ctaMutedTextColor || undefined,
                  }}
                  className="text-gray-600"
                >
                  {cfg.successSubtitle}
                </p>
              </div>
            ) : (
              <>
                {activeTab === "whatsapp" && (
                  <div className="space-y-6">
                    <div style={panelStyle} className="bg-green-50 rounded-xl p-6">
                      <div className="flex items-center mb-6">
                        <div
                          style={{
                            backgroundColor: style?.ctaAccentBg || undefined,
                            color: style?.ctaAccentText || undefined,
                            borderColor: style?.ctaAccentBorder || undefined,
                            borderWidth: style?.ctaAccentBorder ? "1px" : undefined,
                          }}
                          className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-green-600 text-white"
                        >
                          <MessageCircle className="w-6 h-6" />
                        </div>
                        <div>
                          <h3
                            style={{ color: style?.ctaInfoTitleColor || undefined }}
                            className="text-xl font-bold text-gray-900"
                          >
                            {cfg.whatsappCardTitle}
                          </h3>
                          <p
                            style={{ color: style?.ctaInfoTextColor || undefined }}
                            className="text-green-600"
                          >
                            {cfg.whatsappCardSubtitle}
                          </p>
                        </div>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-4">
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder={cfg.namePlaceholder}
                          style={inputStyle}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition"
                          required
                        />

                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder={cfg.phonePlaceholder}
                          style={inputStyle}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition"
                          required
                        />

                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows="4"
                          placeholder={cfg.messagePlaceholder}
                          style={inputStyle}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition resize-none"
                          required
                        />

                        {(cfg.whatsappTemplates || []).length > 0 && (
                          <div className="grid grid-cols-2 gap-3">
                            {(cfg.whatsappTemplates || []).map((template, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => setFormData((p) => ({ ...p, message: template }))}
                                style={quickItemStyle}
                                className={`border px-4 py-3 rounded-xl text-sm transition-all ${
                                  direction === "rtl" ? "text-right" : "text-left"
                                } bg-white hover:opacity-90`}
                              >
                                {template}
                              </button>
                            ))}
                          </div>
                        )}

                        {showPrimary && (
                          <button
                            type="submit"
                            style={
                              primaryHasCustom
                                ? {
                                    backgroundColor: style?.primaryBtnBg || undefined,
                                    color: style?.primaryBtnText || undefined,
                                  }
                                : undefined
                            }
                            className={`w-full font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-center text-lg ${
                              primaryHasCustom ? "hover:opacity-90" : "bg-green-600 hover:bg-green-700 text-white"
                            }`}
                          >
                            <Send className="w-5 h-5 mr-2" />
                            {primaryLabel}
                          </button>
                        )}
                      </form>
                    </div>

                    <div style={infoCardStyle} className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center justify-between text-sm">
                        <span
                          style={{ color: style?.ctaFooterTextColor || style?.ctaMutedTextColor || undefined }}
                          className="text-gray-600"
                        >
                          📱 {cfg.whatsappFooterLine}
                        </span>
                        <span
                          style={{ color: style?.ctaInfoTextColor || undefined }}
                          className="text-green-600 font-medium"
                        >
                          Online now
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "phone" && (
                  <div className="space-y-6">
                    <div style={panelStyle} className="bg-blue-50 rounded-xl p-6">
                      <h3
                        style={{ color: style?.ctaInfoTitleColor || undefined }}
                        className="text-xl font-bold text-gray-900 mb-4"
                      >
                        {cfg.phoneTitle}
                      </h3>

                      <div style={infoCardStyle} className="bg-white rounded-lg p-6 mb-6">
                        <div className="text-center">
                          <div
                            style={{ color: style?.ctaInfoTitleColor || undefined }}
                            className="text-3xl font-bold text-gray-900 mb-2"
                          >
                            +{targetPhone || "----"}
                          </div>
                          <p
                            style={{ color: style?.ctaInfoTextColor || style?.ctaMutedTextColor || undefined }}
                            className="text-gray-600"
                          >
                            {cfg.phoneHours}
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={handlePhoneCall}
                        style={
                          primaryHasCustom
                            ? {
                                backgroundColor: style?.primaryBtnBg || undefined,
                                color: style?.primaryBtnText || undefined,
                              }
                            : undefined
                        }
                        className={`w-full font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-center ${
                          primaryHasCustom ? "hover:opacity-90" : "bg-blue-600 hover:bg-blue-700 text-white"
                        }`}
                      >
                        <Phone className="w-5 h-5 mr-2" />
                        {cfg.phoneCallNowText}
                      </button>

                      <div className="mt-6 text-sm text-center">
                        <p
                          style={{ color: style?.ctaMutedTextColor || undefined }}
                          className="text-gray-500"
                        >
                          <Clock className="inline-block w-4 h-4 mr-1" />
                          {cfg.phoneRatesText}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div
            style={footerBarStyle}
            className="bg-gray-50 px-8 py-4 border-t grid grid-cols-3 gap-4"
          >
            <div className="flex items-center justify-center text-sm">
              <Globe className="w-4 h-4 mr-2" />
              <span>{cfg.footerStats?.[0] || ""}</span>
            </div>
            <div className="flex items-center justify-center text-sm">
              <Users className="w-4 h-4 mr-2" />
              <span>{cfg.footerStats?.[1] || ""}</span>
            </div>
            <div className="flex items-center justify-center text-sm">
              <Shield className="w-4 h-4 mr-2" />
              <span>{cfg.footerStats?.[2] || ""}</span>
            </div>
          </div>
        </div>

        <div
          style={{ color: style?.ctaFooterTextColor || undefined }}
          className={`mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-white/80 ${
            hasAlign ? toAlignClass(align) : "text-center"
          }`}
        >
          {(cfg.trustBadges || []).map((b, i) => (
            <div key={i}>{b}</div>
          ))}
        </div>
      </div>
    </section>
  );
}